import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";
import {
  ApprovalSearchType,
  GetListOfApprovalTemporaryStorageQueryVariables,
  ListOfApprovalTemporaryStorageEntity,
  useCountListOfApprovalTemporaryStorageLazyQuery,
  useGetListOfApprovalTemporaryStorageLazyQuery
} from "../../generated/graphql";
import usePageControl from "../../hooks/use-page-control/use-page-control";
import EChannel from "../../Utils/EChannel/e-channel";
import { SearchFieldValues } from "../asonic-table/asonic-search";
import * as ReactTable from "react-table";
import { Cell } from "../../../types/@react-table/react-table/react-table";
import routes from "../../routes";
import { Icon } from "@iconify/react";
import ExternalLinkIcon from "@iconify-icons/eva/external-link-outline";
import { TColumn } from "../../hooks/use-hide-columns/use-hide-columns";
import useDnd from "../../hooks/use-dnd/use-dnd";
import { useReactiveVar } from "@apollo/client";
import listOfApolloVar from "../../apollo/apollo-var";
import ApprovalRequestDetailPopup from "./approval-request-detail-popup/approval-request-detail-popup";
import { ApprovalType, IApprovalDetailMessage } from "./approval-detail-popup";
import { client } from "../../apollo/apollo";
import useNewSortBy from "../../hooks/use-new-sort-by/use-new-sort-by";
import NewAsonicTable from "../new-asonic-table/new-asonic-table";
import useFixedColumn from "../../hooks/use_fixed_column/use_fixed_column";
import { useSticky } from "react-table-sticky";
import ApprovalListFormHeader from "./approval-list/approval_list_form_header";
import TableLayoutContainer from "../table_layout/table_layout_container";
import { AutoSizer } from "react-virtualized";
import TableV2 from "../table_v2/table_v2";
import TableLayoutFooter from "../table_layout/table_layout_footer";
import PageController from "../table/page_controller";
import { TOKEN } from "../../apollo/apollo";
import { handleDecodedToken } from "../../Utils/tokenMaker";
import {
  listWorkPolicyApplicationDrafts,
  WorkPolicyApplicationDraftListItem
} from "./approval-form/work-policy/work-policy-application-draft-index";

export const ENUM_COMMON_LIST_OF_APPROVAL = {
  signUpDatetime: "작성일",
  formIdx: "양식",
  approvalTitle: "제목",
  attachedFileName: "첨부"
} as const;

export type ENUM_COMMON_LIST_OF_APPROVAL =
  (typeof ENUM_COMMON_LIST_OF_APPROVAL)[keyof typeof ENUM_COMMON_LIST_OF_APPROVAL];

export type TYPE_COMMON_LIST_OF_APPROVAL =
  keyof typeof ENUM_COMMON_LIST_OF_APPROVAL;

const Container = styled.div`
  display: flex;
  flex: 1;
`;

export const Item = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: 5px;
  span {
    cursor: pointer;
    :hover {
      color: ${props => props.theme.colors.lightGreen};
    }
    :active {
      transform: scale(0.98);
    }
  }
`;

export const ItemForTitle = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  padding: 0px 5px;
  span {
    cursor: pointer;
    :hover {
      color: ${props => props.theme.colors.lightGreen};
    }
    :active {
      transform: scale(0.98);
    }
  }
`;

export const IconContainer = styled.div`
  cursor: pointer;
  svg {
    font-size: 20px;
  }
  :hover {
    color: ${props => props.theme.colors.lightGreen};
  }
  :active {
    transform: scale(0.95);
  }
`;

const Content = styled.div<{ showApprovalPopup: boolean }>`
  display: flex;
  ${props => props.showApprovalPopup !== true && "height: 96%;"}
  padding: 10px;
  flex: 1;
  min-width: 790px;
  overflow-x: hidden;
  flex-direction: column;
`;

const PopUpContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 50%;
`;

const title = "임시 저장함";

function ApprovalTemporaryDocument() {
  const isRequest = useRef<boolean>(false);
  const approvalTemporaryStorage = useReactiveVar(
    listOfApolloVar.approvalTemporaryStorageVar
  );
  const approvalType = useReactiveVar(listOfApolloVar.approvalTypeVar);
  const FormName = useReactiveVar(listOfApolloVar.approvalFormNameVar);
  const [showApprovalPopup, setShowApprovalPopup] = useState<boolean>(false);
  const employeeId = useMemo((): string => {
    const token = localStorage.getItem(TOKEN);
    if (!token) return "";
    const decodedData: any = handleDecodedToken(token);
    return decodedData.employee_id ?? "";
  }, []);
  const [workPolicyDrafts, setWorkPolicyDrafts] = useState<
    WorkPolicyApplicationDraftListItem[]
  >([]);
  const refreshWorkPolicyDrafts = useCallback(() => {
    setWorkPolicyDrafts(
      employeeId
        ? listWorkPolicyApplicationDrafts(window.localStorage, employeeId)
        : []
    );
  }, [employeeId]);
  const { fieldSort, handleFieldSort } = useNewSortBy();
  const [selectedRow, setSelectedRow] = useState<
    ListOfApprovalTemporaryStorageEntity | undefined
  >(undefined);

  const {
    fixedColumnNumber,
    selectedFixedColumnNumber,
    handleSelectedFCN,
    sFixedColumnNumber,
    fixedColumnName,
    setFixedColumnName,
    isActiveFilter,
    setIsActiveFilter
  } = useFixedColumn();

  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);

  const handleSelectedRowIndex = useCallback((index: number) => {
    setSelectedRowIndex(index);
  }, []);

  const handleShowApprovalPopup = useCallback((value: boolean) => {
    setShowApprovalPopup(value);
  }, []);

  const handleSelectedRow = useCallback(
    (row: ListOfApprovalTemporaryStorageEntity) => {
      setSelectedRow(row);
    },
    []
  );

  const [searchType, setSearchType] = useState<ApprovalSearchType>(
    ApprovalSearchType.ApprovalForm
  );

  const [searchValue, setSearchValue] = useState("");

  const { currentPage, handleCurrentPage, take, handleTake } = usePageControl();

  const handleSearch = useCallback(
    (data: SearchFieldValues) => {
      if (data.type) {
        const INIT_PAGE = 1;
        setSearchType(data.type as ApprovalSearchType);
        setSearchValue(data.value ?? "");
        handleCurrentPage(INIT_PAGE);
        setSearchValue(data.value as string);
      }
    },
    [handleCurrentPage]
  );

  const listOfSearchType = useMemo(() => {
    return [
      {
        value: ApprovalSearchType.ApprovalForm,
        name: "양식"
      },
      {
        value: ApprovalSearchType.Title,
        name: "제목"
      }
    ];
  }, []);

  const bc = useMemo(() => {
    if (selectedRow) {
      return new BroadcastChannel(EChannel.APPROVAL);
    }
    return;
  }, [selectedRow]);

  const bcForReset = useMemo(() => {
    return new BroadcastChannel(EChannel.RESET);
  }, []);

  const [getListOfApprovalTemporaryStorage, { data, loading }] =
    useGetListOfApprovalTemporaryStorageLazyQuery({
      onError(error) {
        console.log(error.message);
      },
      fetchPolicy: "cache-and-network",
      onCompleted(data) {
        if (
          data.getListOfApprovalTemporaryStorage.ok &&
          data.getListOfApprovalTemporaryStorage.list
        ) {
          if (isRequest.current) {
            setSelectedRowIndex(
              data.getListOfApprovalTemporaryStorage.list?.length - 1
            );
            isRequest.current = false;
          }
        }
      }
    });

  const [
    countListOfApprovalTemporaryStorage,
    { data: countData, loading: countLoading }
  ] = useCountListOfApprovalTemporaryStorageLazyQuery({
    fetchPolicy: "cache-and-network"
  });

  const legacyList: ListOfApprovalTemporaryStorageEntity[] = useMemo(() => {
    return data?.getListOfApprovalTemporaryStorage?.list || [];
  }, [data]);

  const visibleWorkPolicyDrafts = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase("ko");
    if (!normalizedSearch) return workPolicyDrafts;
    return workPolicyDrafts.filter(item => {
      const value =
        searchType === ApprovalSearchType.Title
          ? item.approvalTitle ?? ""
          : "근무제 적용 신청";
      return value.toLocaleLowerCase("ko").includes(normalizedSearch);
    });
  }, [searchType, searchValue, workPolicyDrafts]);

  const list: ListOfApprovalTemporaryStorageEntity[] = useMemo(() => {
    if (currentPage !== 1) return legacyList;
    const localIds = new Set(
      visibleWorkPolicyDrafts.map(item => item.approvalRequestIdx)
    );
    return [
      ...visibleWorkPolicyDrafts,
      ...legacyList.filter(item => !localIds.has(item.approvalRequestIdx))
    ].slice(0, take);
  }, [currentPage, legacyList, take, visibleWorkPolicyDrafts]);

  const total: number = useMemo(() => {
    return (
      (countData?.countListOfApprovalTemporaryStorage.count ?? 0) +
      visibleWorkPolicyDrafts.length
    );
  }, [countData, visibleWorkPolicyDrafts.length]);

  const columns: ReactTable.Column<ListOfApprovalTemporaryStorageEntity>[] =
    useMemo(() => {
      const listOfColumn = Object.keys(ENUM_COMMON_LIST_OF_APPROVAL);
      return listOfColumn.map(item => {
        let width = 120;
        if (
          ENUM_COMMON_LIST_OF_APPROVAL[item as TYPE_COMMON_LIST_OF_APPROVAL] ===
          ENUM_COMMON_LIST_OF_APPROVAL.approvalTitle
        ) {
          width = 300;
        }
        let sticky = "";
        if (sFixedColumnNumber !== undefined) {
          if (fixedColumnName.length > 0) {
            fixedColumnName.forEach((fColumn, fColumnIndex) => {
              if (fColumn === item && fColumnIndex < sFixedColumnNumber) {
                sticky = "left";
              }
            });
          }
        }
        return {
          Header:
            ENUM_COMMON_LIST_OF_APPROVAL[item as TYPE_COMMON_LIST_OF_APPROVAL],
          accessor: item as TYPE_COMMON_LIST_OF_APPROVAL,
          width,
          sticky,
          Cell(cell: Cell<ListOfApprovalTemporaryStorageEntity>) {
            if (
              ENUM_COMMON_LIST_OF_APPROVAL[
                item as TYPE_COMMON_LIST_OF_APPROVAL
              ] === ENUM_COMMON_LIST_OF_APPROVAL.approvalTitle
            ) {
              return (
                <ItemForTitle>
                  <span
                    onClick={() => {
                      listOfApolloVar.approvalTemporaryStorageVar(
                        cell?.row?.original
                      );
                      setShowApprovalPopup(true);
                    }}
                  >
                    {cell.value}
                  </span>
                  <IconContainer
                    onClick={() => {
                      handleSelectedRow(cell?.row?.original);
                      window.open(
                        routes.pageRoutes.approvalRequestPopup,
                        "",
                        "width=800, height=880, scrollbars=yes"
                      );
                    }}
                  >
                    <Icon icon={ExternalLinkIcon} />
                  </IconContainer>
                </ItemForTitle>
              );
            }
            if (
              ENUM_COMMON_LIST_OF_APPROVAL[
                item as TYPE_COMMON_LIST_OF_APPROVAL
              ] === ENUM_COMMON_LIST_OF_APPROVAL.formIdx
            ) {
              const newValue = FormName.get(cell.value) ?? "";
              return <Item>{`${newValue}`}</Item>;
            }

            return <Item>{cell.value == null ? "" : `${cell.value}`}</Item>;
          }
        };
      });
    }, [sFixedColumnNumber, fixedColumnName]);

  const table = ReactTable.useTable<ListOfApprovalTemporaryStorageEntity>(
    {
      columns,
      data: list
    },
    ReactTable.useBlockLayout,
    ReactTable.useRowSelect,
    ReactTable.useColumnOrder,
    useSticky
  );

  const { moveColumn } = useDnd<ListOfApprovalTemporaryStorageEntity>({
    columns: table.visibleColumns,
    setColumnOrder: table.setColumnOrder,
    title: `${title}-for-ordering-column`,
    setFixedColumnName
  });

  const totalPage = useMemo(() => Math.ceil(total / take), [total, take]);

  const handleSelectForm = useCallback(
    (isNext: boolean) => {
      if (isNext) {
        if (
          typeof selectedRowIndex === "number" &&
          selectedRowIndex < list.length - 1
        ) {
          const nextIndex = selectedRowIndex + 1;
          handleSelectedRowIndex(nextIndex);
        } else if (currentPage < totalPage) {
          handleSelectedRowIndex(0);
          handleCurrentPage(currentPage + 1);
        }
      } else {
        if (typeof selectedRowIndex === "number" && selectedRowIndex > 0) {
          const preIndex = selectedRowIndex - 1;
          handleSelectedRowIndex(preIndex);
        } else if (currentPage > 1) {
          handleCurrentPage(currentPage - 1);
          if (!isRequest.current) {
            isRequest.current = true;
          }
        }
      }
    },
    [selectedRowIndex, list, handleSelectedRowIndex, currentPage]
  );

  useEffect(() => {
    if (list.length > 0 && !isRequest.current) {
      const selectedRow = list[selectedRowIndex];
      listOfApolloVar.approvalTemporaryStorageVar(selectedRow);
    }
  }, [list, selectedRowIndex]);

  useEffect(() => {
    const payload: GetListOfApprovalTemporaryStorageQueryVariables = {
      searchType,
      searchValue,
      take,
      page: currentPage,
      fieldSort
    };

    getListOfApprovalTemporaryStorage({
      variables: payload
    });
  }, [
    getListOfApprovalTemporaryStorage,
    take,
    currentPage,
    fieldSort,
    searchType,
    searchValue
  ]);

  useEffect(() => {
    const payload = {
      searchType,
      searchValue
    };
    countListOfApprovalTemporaryStorage({
      variables: payload
    });
  }, [countListOfApprovalTemporaryStorage, searchType, searchValue]);

  useEffect(() => {
    if (bc && selectedRow) {
      bc.onmessage = () => {
        const newMessage: IApprovalDetailMessage = {
          type: ApprovalType.APPROVAL_REQUEST_POPUP,
          data: selectedRow,
          isRequest: false,
          isTemporaryStorage: true
        };
        bc.postMessage(newMessage);
        setSelectedRow(undefined);
        bc.close();
      };
    }
    return () => {
      if (bc) {
        bc.close();
      }
    };
  }, [bc, selectedRow]);

  useEffect(() => {
    refreshWorkPolicyDrafts();
  }, [refreshWorkPolicyDrafts]);

  useEffect(() => {
    if (bcForReset) {
      bcForReset.onmessage = event => {
        const message = event.data as IApprovalDetailMessage & {
          workPolicyDraft?: WorkPolicyApplicationDraftListItem;
          removedWorkPolicyDraftId?: number;
        };
        if (message.type === ApprovalType.RESET_CACHE) {
          if (message.workPolicyDraft) {
            setWorkPolicyDrafts(current => [
              message.workPolicyDraft!,
              ...current.filter(
                item =>
                  item.approvalRequestIdx !==
                  message.workPolicyDraft!.approvalRequestIdx
              )
            ]);
          } else if (message.removedWorkPolicyDraftId) {
            setWorkPolicyDrafts(current =>
              current.filter(
                item =>
                  item.approvalRequestIdx !== message.removedWorkPolicyDraftId
              )
            );
          } else {
            refreshWorkPolicyDrafts();
          }
          client.resetStore();
        }
      };
    }
    return () => {
      if (bcForReset) {
        bcForReset.close();
      }
    };
  }, [bcForReset, refreshWorkPolicyDrafts]);

  useEffect(() => {
    const listOfColumn = Object.keys(ENUM_COMMON_LIST_OF_APPROVAL);
    setFixedColumnName(listOfColumn);
  }, [setFixedColumnName]);

  useEffect(() => {
    if (isActiveFilter) {
      setIsActiveFilter(false);
      let newColumns = table.visibleColumns.map(item => item.id);
      setFixedColumnName(newColumns);
    }
  }, [isActiveFilter, table.visibleColumns]);

  return (
    <Container>
      <Content showApprovalPopup={showApprovalPopup}>
        {showApprovalPopup ? (
          <PopUpContainer>
            <ApprovalRequestDetailPopup
              approvalTemporaryStorage={approvalTemporaryStorage}
              newApprovalType={approvalType}
              handleShowApprovalPopup={handleShowApprovalPopup}
              handleSelectForm={handleSelectForm}
            />
          </PopUpContainer>
        ) : (
          <Fragment>
            <ApprovalListFormHeader<ListOfApprovalTemporaryStorageEntity>
              columns={
                table.columns as TColumn<ListOfApprovalTemporaryStorageEntity>[]
              }
              table={table}
              title={title}
              headerTitleList={Object.values(ENUM_COMMON_LIST_OF_APPROVAL)}
              take={take}
              handleTake={handleTake}
              count={total}
              handleCurrentPage={handleCurrentPage}
              fixedColumnNumber={fixedColumnNumber}
              selectedFixedColumnNumber={selectedFixedColumnNumber}
              handleSelectedFCN={handleSelectedFCN}
              listOfSearchType={listOfSearchType}
              handleSearch={handleSearch}
              setIsActiveFilter={setIsActiveFilter}
            />
            <TableLayoutContainer>
              <AutoSizer>
                {({ height, width }) => {
                  return (
                    <TableV2
                      table={table}
                      title={title}
                      fieldSort={fieldSort}
                      handleFieldSort={handleFieldSort}
                      height={height}
                      width={width}
                      moveColumn={moveColumn}
                      loading={loading || countLoading}
                      flexIndex={2}
                    />
                  );
                }}
              </AutoSizer>
            </TableLayoutContainer>
            <TableLayoutFooter>
              <PageController
                currentPage={currentPage}
                totalPage={Math.ceil(total / take)}
                handleCurrentPage={handleCurrentPage}
              />
            </TableLayoutFooter>
          </Fragment>
        )}
      </Content>
    </Container>
  );
}

export default ApprovalTemporaryDocument;
